import React, {useState, useEffect} from "react";
import Box from "@material-ui/core/Box";
import {Workflow, WorkflowInstance} from "common/flowmaster/types/workflow";
import DefinitionSection from "./definition";
import ConfigurationSection from "./configuration";

export interface Definition extends Workflow {
  workflow_instances: WorkflowInstance[];
}

interface DataAssetsCreateViewProps {
  definitions: Definition[];
  isLoadingConfigs: boolean;
  fetchDefinitionConfigs: () => void;
  fetchConfigsError: string | null;
  push: (path: string) => void;
  location: {
    pathname: string;
  };
  params: {
    definitionId?: string;
    configId?: string;
  };
}

const DataAssetsCreateView: React.FC<DataAssetsCreateViewProps> = props => {
  const {
    definitions,
    fetchDefinitionConfigs,
    push,
    location,
    params,
    isLoadingConfigs,
    fetchConfigsError,
  } = props;

  const [selectedDefinitionId, setSelectedDefinitionId] = useState<
    number | string
  >("");
  const [selectedConfigId, setSelectedConfigId] = useState<number | string>("");
  const basePath = location.pathname.split(/\/(create|definition|config)/)[0];

  const selectedDefinition = definitions.find(
    d => d.id === Number(selectedDefinitionId),
  );
  const configurations = selectedDefinition?.workflow_instances || [];

  useEffect(() => {
    if (params.definitionId) {
      const definitionId = parseInt(params.definitionId, 10);
      fetchDefinitionConfigs();
      setSelectedDefinitionId(definitionId);
    } else {
      setSelectedDefinitionId("");
    }
  }, [params.definitionId]);

  useEffect(() => {
    if (params.configId && params.definitionId) {
      const configId = parseInt(params.configId, 10);
      setSelectedConfigId(configId);
    } else if (params.definitionId) {
      setSelectedConfigId("");
    }
  }, [params.configId, params.definitionId]);

  const handleDefinitionChange = (
    event: React.ChangeEvent<{value: unknown}>,
  ) => {
    const definitionId = event.target.value as number;
    setSelectedDefinitionId(definitionId);

    if (definitionId) {
      push(`${basePath}/definition/${definitionId}`);
    } else {
      push(`${basePath}/create`);
    }

    setSelectedConfigId("");
  };

  const handleConfigChange = (event: React.ChangeEvent<{value: unknown}>) => {
    const configId = event.target.value as number;
    setSelectedConfigId(configId);

    if (configId) {
      push(`${basePath}/definition/${selectedDefinitionId}/config/${configId}`);
    } else {
      push(`${basePath}/definition/${selectedDefinitionId}`);
    }
  };

  return (
    <Box style={{padding: "1em"}}>
      <DefinitionSection
        definitions={definitions}
        selectedDefinitionId={selectedDefinitionId}
        handleDefinitionChange={handleDefinitionChange}
      />
      <ConfigurationSection
        selectedConfigId={selectedConfigId}
        selectedDefinition={selectedDefinition}
        configurations={configurations}
        configExpanded={Boolean(selectedDefinitionId)}
        isLoadingConfigs={isLoadingConfigs}
        fetchConfigsError={fetchConfigsError}
        basePath={basePath}
        push={push}
        handleConfigChange={handleConfigChange}
      />
    </Box>
  );
};

export default DataAssetsCreateView;
